import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ClipboardList, Plus, Trash2, Loader2, Pencil, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const ProjectTasks = ({ projectId, user }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');

  const isAdmin = user.role === 'admin' || user.role === 'supervisor';
  const canToggle = user.role !== 'client';

  const fetchTasks = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/tasks`);
      setTasks(r.data);
    } catch (e) {
      console.error('Failed to load tasks', e);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      const r = await axios.post(`${API}/projects/${projectId}/tasks`, { title: newTitle.trim() });
      setTasks((prev) => [...prev, r.data]);
      setNewTitle('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add task');
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (task) => {
    if (!canToggle) return;
    const completed = !task.completed;
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, completed } : t)));
    try {
      await axios.put(`${API}/projects/${projectId}/tasks/${task.id}`, { completed });
    } catch (err) {
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, completed: !completed } : t)));
      toast.error(err.response?.data?.detail || 'Failed to update task');
    }
  };

  const handleSaveEdit = async (task) => {
    const title = editTitle.trim();
    if (!title || title === task.title) {
      setEditingId(null);
      return;
    }
    try {
      await axios.put(`${API}/projects/${projectId}/tasks/${task.id}`, { title });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, title } : t)));
      setEditingId(null);
      toast.success('Task updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update task');
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/tasks/${taskId}`);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      toast.success('Task deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete task');
    }
  };

  const completedCount = tasks.filter((t) => t.completed).length;

  return (
    <div className="glass-card p-4 sm:p-6 mb-6" data-testid="project-tasks-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-orange-500" />
          Projects To Complete
        </h2>
        {tasks.length > 0 && (
          <span className="text-sm text-slate-400 font-medium" data-testid="tasks-progress">
            {completedCount}/{tasks.length} done
          </span>
        )}
      </div>

      {isAdmin && (
        <form onSubmit={handleAdd} className="flex gap-2 mb-4">
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Add a project to complete..."
            className="flex-1 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-500 focus:border-orange-500"
            data-testid="new-task-input"
          />
          <Button
            type="submit"
            disabled={adding || !newTitle.trim()}
            className="bg-gradient-to-r from-orange-500 to-orange-600 min-h-[44px] px-4"
            data-testid="add-task-btn"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            <span className="ml-1 hidden sm:inline">Add</span>
          </Button>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
        </div>
      ) : tasks.length === 0 ? (
        <p className="text-slate-400 text-sm py-4 text-center" data-testid="no-tasks-message">
          No projects added yet{isAdmin ? ' — add one above.' : '.'}
        </p>
      ) : (
        <div className="space-y-2">
          {tasks.map((task) => (
            <div
              key={task.id}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                task.completed
                  ? 'bg-green-50 border-green-300'
                  : 'bg-gray-50 border-gray-200'
              }`}
              data-testid={`task-item-${task.id}`}
            >
              <Checkbox
                checked={task.completed}
                onCheckedChange={() => handleToggle(task)}
                disabled={!canToggle}
                className="w-6 h-6 border-2 border-slate-400 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                data-testid={`task-checkbox-${task.id}`}
              />
              {editingId === task.id ? (
                <>
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit(task);
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    autoFocus
                    className="flex-1 h-9 bg-white border-2 border-orange-400 text-slate-900"
                    data-testid={`edit-task-input-${task.id}`}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleSaveEdit(task)}
                    className="text-green-400 hover:text-green-300 hover:bg-green-500/10 h-8 w-8 p-0"
                    data-testid={`save-task-${task.id}`}
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingId(null)}
                    className="text-slate-400 hover:text-slate-300 hover:bg-slate-500/10 h-8 w-8 p-0"
                    data-testid={`cancel-edit-task-${task.id}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <>
                  <span
                    className={`flex-1 text-sm sm:text-base ${task.completed ? 'text-green-600 line-through' : 'text-gray-900'}`}
                  >
                    {task.title}
                  </span>
                  {canToggle && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingId(task.id);
                          setEditTitle(task.title);
                        }}
                        className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 h-8 w-8 p-0"
                        data-testid={`edit-task-${task.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(task.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                          data-testid={`delete-task-${task.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectTasks;
